import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';

// Base64 image avec les initiales CN (fallback si l'image ne charge pas)
const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiByeD0iMTAwIiBmaWxsPSJ1cmwoI2dyYWRpZW50KSIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJncmFkaWVudCIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiMyNTYzZWI7c3RvcC1vcGFjaXR5OjEiLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdHlsZT0ic3RvcC1jb2xvcjojMWQ0ZWQ4O3N0b3Atb3BhY2l0eToxIi8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeD0iNTAiIHk9IjUwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+Cjx0ZXh0IHg9IjUwIiB5PSI2MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjQwIiBmb250LXdlaWdodD0iYm9sZCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0id2hpdGUiPkNOPC90ZXh0Pgo8L3N2Zz4KPC9zdmc+';

function Header() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.header-nav') && !event.target.closest('.mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 100; // Hauteur du header fixe
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`portfolio-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-content">
        <div className="header-left">
          <img
            src="/baobabsquare-logo.png"
            className="header-brand-logo"
            alt="BaobabSquare"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <img
            src="/1778835220693.png"
            className="profile-photo"
            alt="Cheikh NGOM"
            onError={(e) => {
              console.log('Image failed to load:', e.target.src);
              e.target.src = fallbackImage;
            }}
          />
          <div className="header-info">
            <h1>Cheikh NGOM <span className="header-brand-name">— BaobabSquare</span></h1>
            <h2>{t('header.title')}</h2>
          </div>
        </div>

        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <ul className="nav-links">
            <li><button onClick={() => scrollToSection('hero')}>{t('header.home')}</button></li>
            <li><button onClick={() => scrollToSection('poles')}>{t('header.poles')}</button></li>
            <li><button onClick={() => scrollToSection('partnerships')}>{t('header.partnerships')}</button></li>
            <li><button onClick={() => scrollToSection('about')}>{t('header.about')}</button></li>
            <li><button onClick={() => scrollToSection('vision')}>{t('header.vision')}</button></li>
            <li><button onClick={() => scrollToSection('skills')}>{t('header.skills')}</button></li>
            <li><button onClick={() => scrollToSection('services')}>{t('header.services')}</button></li>
            <li><button onClick={() => scrollToSection('values')}>{t('header.values')}</button></li>
            <li><button onClick={() => scrollToSection('projects')}>{t('header.projects')}</button></li>
            <li><button onClick={() => scrollToSection('experience')}>{t('header.experience')}</button></li>
            <li><button onClick={() => scrollToSection('education')}>{t('header.education')}</button></li>
            <li><button onClick={() => scrollToSection('interests')}>{t('header.interests')}</button></li>
            <li><button onClick={() => scrollToSection('contact')}>{t('header.contact')}</button></li>
          </ul>
          <LanguageSelector />
        </nav>

        <ThemeToggle />

        <button
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu de navigation"
          type="button"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
}

export default Header; 